import React, { useRef, useState, useEffect } from 'react'
import ReactMapGL from 'react-map-gl'
import mapboxgl from 'mapbox-gl'

import { ReactComponent as PinLarge } from '../../../assets/pin-large.svg'
import { COLORS } from '../../../constants/colors'
import { MAP_BOX_ACCESS_TOKEN } from '../../../utils/env'
import { AddJobForm } from '../AddJobForm'
import { ForecastLayers } from '../ForecastLayers'
import { ForecastSlider } from '../ForecastSlider'
import { JobPin } from '../JobPin'

import {
  StyledMarker,
  MapContainer,
  AddJobPopup,
  StyledNavigationControl,
} from './styles'

import 'react-map-gl-geocoder/dist/mapbox-gl-geocoder.css'

// Set at module scope — mapboxgl.accessToken must be configured before the first
// Map constructor call. workerUrl points to the CSP worker served from /public,
// bypassing the blob-URL worker path that fails under webpack 5 (CRA 5).
mapboxgl.accessToken = MAP_BOX_ACCESS_TOKEN
mapboxgl.workerUrl = `${process.env.PUBLIC_URL}/mapbox-gl-csp-worker.js`

const MapView = ({
  mapRef,
  show,
  jobList,
  addJobAddress,
  addJobLocation,
  dispatchSetAddJobLocation,
  dispatchSetSelectedJob,
  viewport,
  onViewportChange,
  onMapClick,
  onClosePopup,
  onAddJob,
  onMapReady,
}) => {
  const containerRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const MAP_HEIGHT = 400
  const [size, setSize] = useState({ width: 800, height: MAP_HEIGHT })

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => {
      const rect = el.getBoundingClientRect()
      const w = Math.round(rect.width) || 800
      // only track width — height is fixed via MAP_HEIGHT
      setSize((prev) => (prev.width === w ? prev : { width: w, height: MAP_HEIGHT }))
    }
    measure()
    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure)
      ro.observe(el)
    }
    window.addEventListener('resize', measure)
    return () => {
      if (ro) ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [show])

  // react-map-gl calls map.remove() in its own componentWillUnmount, but that only
  // removes the canvas from the DOM — it does not synchronously release the WebGL
  // context. GC-based release creates a race: if the new map mounts before GC runs
  // the browser hits its context limit and throws "Failed to initialize WebGL".
  // WEBGL_lose_context.loseContext() forces immediate synchronous release.
  useEffect(() => {
    return () => {
      const map = mapInstanceRef.current
      if (map) {
        try {
          const canvas = map.getCanvas()
          if (canvas) {
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
            if (gl) {
              const ext = gl.getExtension('WEBGL_lose_context')
              if (ext) ext.loseContext()
            }
          }
        } catch (_) { /* ignore */ }
        mapInstanceRef.current = null
      }
    }
  }, [])

  const onMapLoad = () => {
    if (mapRef.current && typeof mapRef.current.getMap === 'function') {
      mapInstanceRef.current = mapRef.current.getMap()
    }
    if (onMapReady) onMapReady()
  }

  const onMapError = (err) => {
    console.error('[ZeusMap] error event', err)
  }

  return (
  <MapContainer show={show} ref={containerRef}>
    <ReactMapGL
      ref={mapRef}
      {...viewport}
      width={size.width}
      height={MAP_HEIGHT}
      mapStyle="mapbox://styles/mapbox/streets-v9"
      onViewportChange={onViewportChange}
      mapboxApiAccessToken={MAP_BOX_ACCESS_TOKEN}
      failIfMajorPerformanceCaveat={false}
      preserveDrawingBuffer={false}
      reuseMaps
      onLoad={onMapLoad}
      onError={onMapError}
      onClick={onMapClick}
    >
      <StyledNavigationControl />

      <ForecastLayers viewport={viewport} />

      {addJobLocation && (
        <StyledMarker
          latitude={addJobLocation.latitude}
          longitude={addJobLocation.longitude}
          offsetLeft={-13}
          offsetTop={-36}
        >
          <PinLarge fill={COLORS.THEME.PRIMARY} />
        </StyledMarker>
      )}
      {jobList.map((job) => (
        <JobPin
          key={job.id}
          job={job}
          onClosePopup={onClosePopup}
          onViewportChange={onViewportChange}
          dispatchSetSelectedJob={dispatchSetSelectedJob}
          dispatchSetAddJobLocation={dispatchSetAddJobLocation}
        />
      ))}
      {addJobLocation && (
        <AddJobPopup
          latitude={addJobLocation.latitude}
          longitude={addJobLocation.longitude}
          offsetTop={-23}
          offsetLeft={17}
          closeButton
          closeOnClick={false}
          onClose={onClosePopup}
          anchor="bottom"
        >
          <AddJobForm address={addJobAddress} onSubmit={onAddJob} />
        </AddJobPopup>
      )}
    </ReactMapGL>
    <ForecastSlider />
  </MapContainer>
  )
}

export { MapView }
